/**
 * Upload-and-extract page (R2, per ADR-0016).
 *
 * One unified entry point for both single and bulk imports:
 *
 *   - `/contracts/import`           — landing: multi-file dropzone +
 *                                     "Senaste importer". Picking 1 file
 *                                     follows the single-import flow;
 *                                     2+ creates a batch and routes to
 *                                     `/contracts/imports/bulk/:batchId`.
 *   - `/contracts/imports/:id`      — single-import tracker (polling,
 *                                     extracted-review panel, confirm).
 *
 * Flow on this page mirrors ADR-0016:
 *   1. user picks a PDF or DOCX
 *   2. `imports.upload_url` → presigned PUT
 *   3. browser PUTs the bytes to S3 (no backend egress)
 *   4. `imports.create` → row + SQS enqueue
 *   5. poll `imports.get` every 2s until terminal status
 *   6. on `extracted` → review panel; user picks a template version
 *      and clicks "Continue" → `imports.confirm` → navigate to editor
 *
 * Failure states are first-class:
 *   - `failed_ocr_required` → "looks like a scanned image" → retry
 *   - `failed_extract`      → generic "we couldn't read this" → retry
 *
 * The review screen surfaces per-party confidence so the user can spot
 * a bad extraction before promoting.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTemplateVersion } from '@hyranai/regta-templates';
import {
  tenantApi,
  type ExtractedPropertiesV1,
  type ImportDetail,
  type TemplateListRow,
} from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { ActiveGroupNotice, useActiveGroup } from '../components/GroupPicker.js';
import { useTenantQuery } from '../query.js';
import { ImportLanding } from '../components/ImportLanding.js';
import { usePageTitle } from '../use-page-title.js';

type LocalStatus =
  | { kind: 'idle' }
  | { kind: 'tracking'; documentId: string }
  | { kind: 'confirming' }
  | { kind: 'error'; message: string };

export function ImportContractPage() {
  const { token } = useTenantSession();
  const navigate = useNavigate();

  // The route `/contracts/imports/:id` mounts this same component with
  // the import's document_id baked into the URL. We seed local status
  // from the URL so refresh / browser-back / share-link all land in
  // tracking mode immediately — no transient "idle" flash.
  const { id: routeImportId } = useParams<{ id: string }>();

  usePageTitle(routeImportId ? 'Importerar avtal' : 'Importera avtal');

  const [status, setStatus] = useState<LocalStatus>(
    routeImportId
      ? { kind: 'tracking', documentId: routeImportId }
      : { kind: 'idle' },
  );
  const [selectedTemplateVersion, setSelectedTemplateVersion] = useState<string | null>(null);

  // Keep `status` in sync with the URL — covers the in-app browser-
  // back from a confirmed contract editor back to the import detail,
  // and the user typing the URL by hand.
  useEffect(() => {
    if (routeImportId) {
      setStatus((prev) =>
        prev.kind === 'tracking' && prev.documentId === routeImportId
          ? prev
          : { kind: 'tracking', documentId: routeImportId },
      );
    } else if (status.kind === 'tracking') {
      // Route flipped back to `/contracts/import` — reset.
      setStatus({ kind: 'idle' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeImportId]);

  const { activeGroupId: groupId } = useActiveGroup();

  const templatesQ = useTenantQuery(
    'templates.list',
    () => tenantApi.templates.list(),
  );
  const templates = templatesQ.data?.templates ?? [];

  // Poll `imports.get` via SWR refresh while tracking. The query is
  // disabled (`enabled: false`) when no doc id is being tracked, so
  // the key flips to null and SWR doesn't fire; flipping back during a
  // tracked import re-keys and starts the poll.
  const trackingId = status.kind === 'tracking' ? status.documentId : null;
  const detailQ = useTenantQuery(
    'imports.get',
    (id) => tenantApi.imports.get(id as string),
    trackingId ? [trackingId] : [],
    {
      enabled: trackingId !== null,
      refreshInterval: trackingId ? 2000 : 0,
      revalidateOnFocus: false,
    },
  );
  const detail: ImportDetail | null = trackingId
    ? (detailQ.data as ImportDetail | undefined) ?? null
    : null;

  useEffect(() => {
    if (detailQ.error && status.kind === 'tracking') {
      setStatus({ kind: 'error', message: (detailQ.error as Error).message });
    }
  }, [detailQ.error, status.kind]);

  // When the extractor lands a `template_type_guess`, preselect the
  // matching template version. The user can still change it.
  useEffect(() => {
    if (!detail?.template_type_guess) return;
    if (selectedTemplateVersion) return;
    const guess = detail.template_type_guess;
    const match = templates.find((t) => t.template_key === guess);
    if (match) setSelectedTemplateVersion(match.version_id);
  }, [detail, templates, selectedTemplateVersion]);

  async function onConfirm() {
    if (!token || !detail || !selectedTemplateVersion) return;
    setStatus({ kind: 'confirming' });
    try {
      const r = await tenantApi.imports.confirm({
        document_id: detail.id,
        template_version_id: selectedTemplateVersion,
      });
      navigate(`/contracts/${r.contract_id}/edit`);
    } catch (e) {
      setStatus({ kind: 'error', message: (e as Error).message });
    }
  }

  // Re-run extraction in place. Used for `failed_extract` where the
  // file is fine but the extractor hit a transient/parse issue. We stay
  // on the same URL — polling picks up the queued → extracting →
  // extracted transition without a navigation.
  async function onRetryExtraction() {
    if (!token || !detail) return;
    try {
      await tenantApi.imports.retry({ id: detail.id });
      await detailQ.mutate();
    } catch (e) {
      setStatus({ kind: 'error', message: (e as Error).message });
    }
  }

  // Soft-delete + leave. For bulk imports we route back to the batch so
  // the user doesn't lose context; for single imports we go to the
  // landing page where they can pick a different file.
  async function onAbandon() {
    if (!token || !detail) return;
    try {
      await tenantApi.imports.discard({ id: detail.id });
    } catch {
      // best-effort
    }
    setSelectedTemplateVersion(null);
    if (detail.batch_id) {
      navigate(`/contracts/imports/bulk/${detail.batch_id}`);
    } else {
      navigate('/contracts/import');
    }
  }

  // Tracker mode wraps the single-import detail flow. The header lives
  // here so error banners and the tracker share one section.
  if (status.kind === 'tracking' || status.kind === 'confirming' || (status.kind === 'error' && trackingId)) {
    return (
      <section className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-sm font-medium text-stone-500">Importera avtal</h2>
          <p className="text-xs text-stone-500">
            Vi läser ut parter, fastighet och datum så du kan granska och spara som avtal.
          </p>
        </header>

        {status.kind === 'error' && (
          <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {status.message}
          </div>
        )}

        {status.kind === 'tracking' && (
          <TrackingView
            detail={detail}
            templates={templates}
            selectedTemplateVersion={selectedTemplateVersion}
            onSelectTemplateVersion={setSelectedTemplateVersion}
            onConfirm={onConfirm}
            onRetryExtraction={onRetryExtraction}
            onAbandon={onAbandon}
          />
        )}

        {status.kind === 'confirming' && (
          <StatusCard title="Skapar avtal" body={<>Sparar parter och fält…</>} />
        )}
      </section>
    );
  }

  // Landing (no route :id) — unified multi-file dropzone + recent imports.
  return (
    <section className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-stone-800">Importera avtal</h2>
        <p className="text-sm text-stone-500">
          Ladda upp signerade hyresavtal som PDF eller DOCX. Vi läser ut parter,
          fastighet och datum så du kan granska och spara som avtal — ett i taget
          eller flera samtidigt.
        </p>
      </header>

      {status.kind === 'error' && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {status.message}
        </div>
      )}

      <ActiveGroupNotice prefix="Importeras till" />

      <ImportLanding groupReady={Boolean(groupId)} />
    </section>
  );
}

function StatusCard({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <div className="rounded border border-stone-200 bg-stone-50 p-6 text-sm">
      <div className="font-medium text-stone-700">{title}</div>
      <div className="mt-1 text-stone-500">{body}</div>
    </div>
  );
}

function TrackingView({
  detail,
  templates,
  selectedTemplateVersion,
  onSelectTemplateVersion,
  onConfirm,
  onRetryExtraction,
  onAbandon,
}: {
  detail: ImportDetail | null;
  templates: TemplateListRow[];
  selectedTemplateVersion: string | null;
  onSelectTemplateVersion: (v: string) => void;
  onConfirm: () => void;
  onRetryExtraction: () => void;
  onAbandon: () => void;
}) {
  if (!detail) {
    return <StatusCard title="Förbereder" body={<>Initierar import…</>} />;
  }
  if (detail.status === 'queued' || detail.status === 'extracting') {
    return (
      <StatusCard
        title="Läser dokumentet"
        body={<>Vi går igenom dokumentet och hämtar uppgifter. Detta tar ofta 10–30 sekunder.</>}
      />
    );
  }
  if (detail.status === 'failed_ocr_required') {
    // Re-running extraction on the same bytes won't help — the
    // extractor already classified it as image-based. The only useful
    // path is to abandon this import and upload a different file.
    return (
      <FailureCard
        title="Det här ser ut som en skannad bild"
        message="Vi kan inte läsa text från bildbaserade PDF:er. Ladda upp en text-baserad PDF eller DOCX, eller skapa avtalet från en mall."
        primary={{
          label: detail.batch_id ? 'Ta bort och gå tillbaka' : 'Ladda upp annan fil',
          onClick: onAbandon,
        }}
      />
    );
  }
  if (detail.status === 'failed_extract') {
    if (detail.error_message === 'unsupported_template_type') {
      return (
        <UnsupportedTemplateCard
          onRetry={onRetryExtraction}
          onAbandon={onAbandon}
          abandonLabel={detail.batch_id ? 'Ta bort och gå tillbaka' : 'Avbryt'}
        />
      );
    }
    return (
      <FailureCard
        title="Kunde inte tolka dokumentet"
        message={detail.error_message ?? 'Ett okänt fel uppstod under extraktionen.'}
        primary={{ label: 'Försök igen', onClick: onRetryExtraction }}
        secondary={{
          label: detail.batch_id ? 'Ta bort och gå tillbaka' : 'Avbryt',
          onClick: onAbandon,
        }}
      />
    );
  }
  if (detail.status === 'extracted') {
    return (
      <ExtractedReview
        detail={detail}
        templates={templates}
        selectedTemplateVersion={selectedTemplateVersion}
        onSelectTemplateVersion={onSelectTemplateVersion}
        onConfirm={onConfirm}
        onRetry={onAbandon}
      />
    );
  }
  return null;
}

function FailureCard({
  title,
  message,
  primary,
  secondary,
}: {
  title: string;
  message: string;
  primary: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
}) {
  return (
    <div className="space-y-3 rounded border border-red-200 bg-red-50 p-6">
      <div className="text-sm font-medium text-red-800">{title}</div>
      <div className="text-sm text-red-700">{message}</div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={primary.onClick}
          className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
        >
          {primary.label}
        </button>
        {secondary && (
          <button
            type="button"
            onClick={secondary.onClick}
            className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-50"
          >
            {secondary.label}
          </button>
        )}
      </div>
    </div>
  );
}

function UnsupportedTemplateCard({
  onRetry,
  onAbandon,
  abandonLabel,
}: {
  onRetry: () => void;
  onAbandon: () => void;
  abandonLabel: string;
}) {
  return (
    <div className="space-y-3 rounded border border-amber-200 bg-amber-50 p-6">
      <div className="text-sm font-medium text-amber-900">
        Dokumenttypen stöds inte
      </div>
      <div className="text-sm text-amber-800">
        AI:n kunde inte identifiera dokumentet som något av de avtal vi
        kan läsa idag. Om du tror att detta är ett misstag — försök igen.
      </div>
      <div className="text-sm text-amber-800">
        Vi stödjer just nu följande avtalstyper för uppladdning:
        <ul className="mt-1 list-disc pl-5">
          <li>Bostadshyresavtal</li>
          <li>Lokalhyresavtal</li>
          <li>Förhandlingsordning</li>
          <li>Förhandlingsöverenskommelse</li>
        </ul>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
        >
          Försök igen
        </button>
        <button
          type="button"
          onClick={onAbandon}
          className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-50"
        >
          {abandonLabel}
        </button>
      </div>
    </div>
  );
}

function ExtractedReview({
  detail,
  templates,
  selectedTemplateVersion,
  onSelectTemplateVersion,
  onConfirm,
  onRetry,
}: {
  detail: ImportDetail;
  templates: TemplateListRow[];
  selectedTemplateVersion: string | null;
  onSelectTemplateVersion: (v: string) => void;
  onConfirm: () => void;
  onRetry: () => void;
}) {
  const ex = detail.extracted_properties;
  const lowConfidence = (ex?.confidence_overall ?? 1) < 0.6;
  return (
    <div className="space-y-4">
      <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
        Extraktionen klar. Granska fälten nedan och välj mall för avtalet.
      </div>
      {lowConfidence && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Låg konfidens på extraktionen ({Math.round((ex?.confidence_overall ?? 0) * 100)}%).
          Granska fälten extra noga.
        </div>
      )}

      {ex && ex.warnings.length > 0 && (
        <div className="space-y-2">
          {ex.warnings
            .filter((w) => w.severity === 'incomplete_required')
            .map((w, i) => (
              <div
                key={`req-${i}`}
                className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800"
              >
                <span className="font-medium">Saknas — måste kompletteras: </span>
                {w.message}
              </div>
            ))}
          {ex.warnings
            .filter((w) => w.severity === 'warning')
            .map((w, i) => (
              <div
                key={`warn-${i}`}
                className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
              >
                {w.message}
              </div>
            ))}
          {ex.warnings
            .filter((w) => w.severity === 'info')
            .map((w, i) => (
              <div
                key={`info-${i}`}
                className="rounded border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600"
              >
                {w.message}
              </div>
            ))}
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-xs font-medium text-stone-600">Mall</label>
        <select
          value={selectedTemplateVersion ?? ''}
          onChange={(e) => onSelectTemplateVersion(e.target.value)}
          className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
        >
          <option value="">Välj mall…</option>
          {templates.map((t) => {
            // Prefer the registry's `shortTitle` so the picker reads
            // "Bostadshyresavtal" rather than the versioned document
            // title ("Bostadshyresavtal - Version 2026"). Matches the
            // contract list. Falls back to `t.name` when the
            // registered template module isn't loaded for some reason
            // (would only happen mid-version-rollout).
            const short =
              getTemplateVersion(t.template_key, t.version)?.metadata.shortTitle ??
              t.name;
            return (
              <option key={t.version_id} value={t.version_id}>
                {short}
              </option>
            );
          })}
        </select>
        {detail.template_type_guess && detail.template_type_guess !== 'unknown' && (
          <p className="text-xs text-stone-500">
            AI-gissning: <span className="font-medium">{detail.template_type_guess}</span>
            {detail.supported === false && ' (vi har ingen mall för den här typen ännu)'}
          </p>
        )}
      </div>

      {ex && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Section title="Parter">
            {ex.parties.length === 0 ? (
              <div className="text-xs text-stone-500">Inga parter hittades.</div>
            ) : (
              <ul className="space-y-2">
                {ex.parties.map((p, i) => (
                  <li key={i} className="rounded border border-stone-200 bg-white p-2 text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium">{p.legal_name}</div>
                      <ConfidenceBadge value={p.confidence} />
                    </div>
                    <div className="text-xs text-stone-500">
                      {p.role}
                      {p.ordinal !== null && ` #${p.ordinal}`}
                      {p.id_number && ` · ${p.id_number}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <PropertyEntityPreview property={ex.property} />

          <Section title="Datum">
            <DetailRow label="Startdatum" value={ex.dates.start} />
            <DetailRow label="Slutdatum" value={ex.dates.end} />
            <DetailRow label="Signeringsdatum" value={ex.dates.signing} />
            <DetailRow
              label="Uppsägningstid"
              value={
                ex.dates.notice_period_months !== null
                  ? `${ex.dates.notice_period_months} mån`
                  : null
              }
            />
          </Section>

          <Section title="Hyra">
            <DetailRow
              label="Hyra / mån"
              value={
                ex.rent.monthly_sek
                  ? `${ex.rent.monthly_sek.toLocaleString('sv-SE')} kr`
                  : null
              }
            />
            <DetailRow
              label="Hyra / år"
              value={
                ex.rent.yearly_sek
                  ? `${ex.rent.yearly_sek.toLocaleString('sv-SE')} kr`
                  : null
              }
            />
            <DetailRow label="Angiven som" value={ex.rent.period} />
          </Section>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50"
        >
          Avbryt
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!selectedTemplateVersion}
          className="rounded bg-stone-900 px-4 py-1.5 text-sm font-medium text-white disabled:bg-stone-400"
        >
          Skapa avtal
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-stone-200 bg-white p-3">
      <div className="mb-2 text-xs font-medium text-stone-500">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-sm">
      <span className="text-xs text-stone-500">{label}</span>
      <span className={value ? 'text-stone-800' : 'text-stone-400'}>{value ?? '—'}</span>
    </div>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone = value >= 0.8 ? 'bg-green-100 text-green-800' : value >= 0.5 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${tone}`}>{pct}%</span>
  );
}

/**
 * Pre-confirm property preview — renders the extracted property as a
 * discrete entity card so the reviewer reads it the same way the
 * post-confirm view will, and includes a heuristic note about what
 * will happen on `Skapa avtal`. The actual decision (link to
 * existing / create new / leave pending) runs server-side at confirm
 * time; this is a client-side hint based purely on which fields the
 * extractor populated.
 */
function PropertyEntityPreview({
  property,
}: {
  property: ExtractedPropertiesV1['property'];
}) {
  const designation = property.designation?.trim() ?? '';
  const street = property.street?.trim() ?? '';
  const postal = property.postal_code?.trim() ?? '';
  const ort = property.ort?.trim() ?? '';

  const happyCase = Boolean(designation && street && postal && ort);
  const hasAnyData = Boolean(designation || street || postal || ort);

  let hint: { tone: 'ok' | 'warn' | 'muted'; text: string };
  if (!hasAnyData) {
    hint = {
      tone: 'muted',
      text: 'Ingen fastighetsinfo extraherad. Koppla manuellt efter att avtalet skapats.',
    };
  } else if (happyCase) {
    hint = {
      tone: 'ok',
      text:
        'Tillräcklig data — fastigheten skapas eller kopplas automatiskt vid Skapa avtal. ' +
        'Eventuell befintlig fastighet med samma beteckning återanvänds.',
    };
  } else {
    const missing = [
      !designation && 'beteckning',
      !street && 'gatuadress',
      !postal && 'postnummer',
      !ort && 'ort',
    ]
      .filter(Boolean)
      .join(', ');
    hint = {
      tone: 'warn',
      text: `Ofullständig data (saknar: ${missing}). Koppla manuellt efter Skapa avtal.`,
    };
  }

  const toneClass =
    hint.tone === 'ok'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
      : hint.tone === 'warn'
        ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-stone-200 bg-stone-50 text-stone-600';

  return (
    <Section title="Fastighet">
      <div className="space-y-1">
        <div className="text-sm font-medium text-stone-800">
          {designation || <span className="text-stone-400">— ingen beteckning —</span>}
        </div>
        <div className={`text-xs ${street ? 'text-stone-700' : 'text-stone-400'}`}>
          {street || '— ingen gatuadress —'}
        </div>
        <div className={`text-xs ${postal || ort ? 'text-stone-700' : 'text-stone-400'}`}>
          {[postal, ort].filter(Boolean).join(' ') || '— ingen ort —'}
        </div>
        {(property.unit_number || property.unit_size_sqm || property.room_count) && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-stone-100 pt-2 text-[11px] text-stone-600">
            {property.unit_number && <span>Lgh.nr {property.unit_number}</span>}
            {property.unit_size_sqm !== null && property.unit_size_sqm !== undefined && (
              <span>{property.unit_size_sqm} m²</span>
            )}
            {property.room_count && <span>{property.room_count}</span>}
            {property.floor && <span>vån. {property.floor}</span>}
          </div>
        )}
      </div>
      <div className={`mt-3 rounded border px-2 py-1.5 text-[11px] ${toneClass}`}>{hint.text}</div>
    </Section>
  );
}
